import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ClipboardCheck, ArrowRight, ArrowLeft, CheckCircle2, Scale, Gift, ShieldCheck, FileCheck2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import ComplianceStrip from "../components/ComplianceStrip";
import NextStepPromptCard from "../components/NextStepPromptCard";
import PartnerConsentDialog from "../components/PartnerConsentDialog";
import Seo from "../components/Seo";
import SeoProse from "../components/SeoProse";
import { api, track } from "../lib/api";
import { fireMetaLeadOnce } from "../lib/metaPixel";
import { PAGES } from "../content/site";

/* IMPORTANT — e.surv integration is unchanged.
   This page is a presentation-layer refactor only. The form still collects the
   exact same fields and still POSTs the identical object
   ({ ...form, consent: true, source }) to /survey/lead. No field names, data
   types, mappings, payload structure or submission route have changed. */

const FALLBACK = {
  preferred_services: ["HomePlus Digital Survey (Level 2)", "HomePlus Digital Survey with Valuation (Level 2)", "Home Survey Level 3", "Independent Valuation", "Help to Buy Valuation"],
  property_types: ["Detached", "Semi-detached", "Terraced", "Mid-terraced", "End-terraced", "Flat", "Bungalow", "Purpose Built", "Converted", "Unknown"],
  property_tenures: ["Freehold", "Leasehold"],
  property_listed: ["Yes", "No"],
  salutations: ["Mr", "Ms", "Miss", "Mrs", "Dr", "Prof", "Rev", "Sir", "Lady", "Lord"],
};

const initialForm = {
  Salutation: "",
  First_Name: "",
  Last_Name: "",
  Email: "",
  Telephone: "",
  Preferred_Service: "",
  Service_Type: "Survey",
  Property_Value: "",
  Property_Bedrooms: "",
  Property_Type: "",
  Property_Tenure: "",
  Property_Listed: "",
  Property_Link: "",
  Job_Address_Line_1: "",
  Job_Address_Line_2: "",
  Job_Address_Town_City: "",
  Job_Address_County: "",
  Job_Address_Postcode: "",
  Additional_information: "",
};

const NEXT_STEPS = [
  "A RICS-regulated surveyor will review your details and prepare a no-obligation quote.",
  "They'll be in touch using the details you provided — keep an eye on your email and phone.",
  "There's no commitment: you decide whether to go ahead once you've seen the quote.",
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const ErrorText = ({ msg }) =>
  msg ? <span className="block mt-1 text-xs text-[#B91C1C]" role="alert">{msg}</span> : null;

const Text = ({ label, name, value, onChange, error, required, type = "text", placeholder, hint, inputMode, autoComplete }) => (
  <label className="block">
    <span className="mp-label">{label}{required && <span className="text-[#0F766E]"> *</span>}</span>
    <input
      type={type}
      inputMode={inputMode}
      autoComplete={autoComplete}
      className="mp-input"
      value={value ?? ""}
      placeholder={placeholder}
      aria-invalid={error ? "true" : undefined}
      onChange={(e) => onChange(name, e.target.value)}
      data-testid={`sq-${name}`}
    />
    {hint && !error && <span className="mp-help">{hint}</span>}
    <ErrorText msg={error} />
  </label>
);

const SelectField = ({ label, name, value, onChange, options, error, required, placeholder }) => (
  <label className="block">
    <span className="mp-label">{label}{required && <span className="text-[#0F766E]"> *</span>}</span>
    <select className="mp-input" value={value ?? ""} aria-invalid={error ? "true" : undefined} onChange={(e) => onChange(name, e.target.value)} data-testid={`sq-${name}`}>
      <option value="">{placeholder || "Select…"}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
    <ErrorText msg={error} />
  </label>
);

/* ---------------- Hero (above the fold) ---------------- */
const REASSURANCE = [
  { icon: FileCheck2, text: "Free survey quote" },
  { icon: ShieldCheck, text: "RICS-regulated survey provider" },
  { icon: Gift, text: "£50 cashback after survey completion" },
];

const Hero = ({ onStart }) => (
  <section className="rounded-2xl border border-[#0F766E]/20 bg-gradient-to-br from-[#0F766E]/6 via-white to-[#10B981]/8 p-6 sm:p-8" data-testid="sq-hero">
    <div className="text-xs uppercase tracking-widest text-[#0F766E] flex items-center gap-2">
      <ClipboardCheck className="w-3.5 h-3.5" /> Property survey quote
    </div>
    <h1 className="font-sora mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B1220]">
      Get a <span className="text-[#0B5F58]">£50 cashback</span> when you complete your property survey through MortgagePath
    </h1>
    <p className="mt-4 text-[#475569] leading-relaxed max-w-2xl">
      Request your survey quote through MortgagePath. There's no obligation, and if you go ahead and complete your
      survey through our survey provider, we'll pay you £50 cashback. Eligibility and T&amp;Cs apply.
    </p>

    <ul className="mt-5 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-x-6 sm:gap-y-2">
      {REASSURANCE.map(({ icon: Icon, text }) => (
        <li key={text} className="flex items-center gap-2 text-sm text-[#0B1220]">
          <Icon className="w-4 h-4 text-[#0F766E] shrink-0" aria-hidden="true" />
          <span>{text}</span>
        </li>
      ))}
    </ul>

    <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
      <button type="button" onClick={onStart} className="mp-btn-primary w-full sm:w-auto justify-center" data-testid="sq-hero-cta">
        Get my survey quote <ArrowRight className="w-4 h-4" />
      </button>
      <p className="text-xs text-[#94A3B8] leading-relaxed">
        The £50 cashback is a MortgagePath reward and doesn't affect the survey service or the surveyor's recommendations.{" "}
        <Link to="/survey-reward-terms" className="underline hover:text-[#0F766E]" data-testid="sq-reward-terms">£50 cashback terms apply</Link>.
      </p>
    </div>
  </section>
);

/* ---------------- Progress indicator ---------------- */
const STEP_TITLES = ["The property", "Your survey", "Your details"];
const ProgressBar = ({ step }) => (
  <div data-testid="sq-progress">
    <div className="flex items-center justify-between">
      <span className="text-sm font-semibold text-[#0B1220]">Step {step} of 3</span>
      <span className="text-xs text-[#475569]">{STEP_TITLES[step - 1]}</span>
    </div>
    <div className="mt-2 flex gap-1.5" aria-hidden="true">
      {[1, 2, 3].map((n) => (
        <span key={n} className={`h-1.5 flex-1 rounded-full ${n <= step ? "bg-[#0F766E]" : "bg-[#DCE7E3]"}`} />
      ))}
    </div>
  </div>
);

/* ---------------- Step panels (same fields, grouped) ---------------- */
const StepProperty = ({ form, set, errors, opts }) => (
  <div className="grid sm:grid-cols-2 gap-5">
    <label className="block sm:col-span-2">
      <span className="mp-label">Property address line 1<span className="text-[#0F766E]"> *</span></span>
      <input type="text" className="mp-input" autoComplete="address-line1" value={form.Job_Address_Line_1} aria-invalid={errors.Job_Address_Line_1 ? "true" : undefined} onChange={(e) => set("Job_Address_Line_1", e.target.value)} data-testid="sq-Job_Address_Line_1" />
      <ErrorText msg={errors.Job_Address_Line_1} />
    </label>
    <Text label="Address line 2" name="Job_Address_Line_2" value={form.Job_Address_Line_2} onChange={set} error={errors.Job_Address_Line_2} autoComplete="address-line2" />
    <Text label="Town / City" name="Job_Address_Town_City" value={form.Job_Address_Town_City} onChange={set} error={errors.Job_Address_Town_City} required autoComplete="address-level2" />
    <Text label="County" name="Job_Address_County" value={form.Job_Address_County} onChange={set} error={errors.Job_Address_County} autoComplete="address-level1" />
    <Text label="Postcode" name="Job_Address_Postcode" value={form.Job_Address_Postcode} onChange={set} error={errors.Job_Address_Postcode} required placeholder="e.g. SW1A 1AA" autoComplete="postal-code" hint="So the surveyor knows where the property is." />
    <SelectField label="Property type" name="Property_Type" value={form.Property_Type} onChange={set} options={opts.property_types} error={errors.Property_Type} placeholder="Optional" />
    <Text label="Estimated property value (£)" name="Property_Value" value={form.Property_Value} onChange={set} error={errors.Property_Value} inputMode="numeric" placeholder="e.g. 300,000" hint="Helps the surveyor prepare an accurate quote." />
    <Text label="Number of bedrooms" name="Property_Bedrooms" value={form.Property_Bedrooms} onChange={set} error={errors.Property_Bedrooms} inputMode="numeric" placeholder="e.g. 3" />
    <SelectField label="Tenure" name="Property_Tenure" value={form.Property_Tenure} onChange={set} options={opts.property_tenures} error={errors.Property_Tenure} placeholder="Optional" />
    <SelectField label="Listed building?" name="Property_Listed" value={form.Property_Listed} onChange={set} options={opts.property_listed} error={errors.Property_Listed} placeholder="Optional" />
    <label className="block sm:col-span-2">
      <span className="mp-label">Property listing link (optional)</span>
      <input type="url" className="mp-input" inputMode="url" value={form.Property_Link} placeholder="https://…" onChange={(e) => set("Property_Link", e.target.value)} data-testid="sq-Property_Link" />
      <span className="mp-help">A Rightmove/Zoopla link can help the surveyor understand the property.</span>
      <ErrorText msg={errors.Property_Link} />
    </label>
  </div>
);

const StepSurvey = ({ form, set, errors, opts }) => (
  <div className="grid gap-5">
    <SelectField label="Type of survey" name="Preferred_Service" value={form.Preferred_Service} onChange={set} options={opts.preferred_services} error={errors.Preferred_Service} required placeholder="Choose a survey type…" />
    <p className="text-xs text-[#475569] leading-relaxed -mt-2">
      Not sure which survey you need? Choose your best guess — the surveyor can advise you on the most suitable option for your property.
    </p>
    <label className="block">
      <span className="mp-label">Notes for the surveyor (optional)</span>
      <textarea className="mp-input" rows={3} value={form.Additional_information} placeholder="e.g. access via rear, tenant in situ, specific concerns" onChange={(e) => set("Additional_information", e.target.value)} data-testid="sq-Additional_information" />
      <span className="mp-help">Anything that would help the surveyor prepare your quote.</span>
    </label>
  </div>
);

const StepContact = ({ form, set, errors, opts }) => (
  <div className="grid sm:grid-cols-2 gap-5">
    <SelectField label="Title" name="Salutation" value={form.Salutation} onChange={set} options={opts.salutations} placeholder="Optional" />
    <div className="hidden sm:block" aria-hidden="true" />
    <Text label="First name" name="First_Name" value={form.First_Name} onChange={set} error={errors.First_Name} required autoComplete="given-name" />
    <Text label="Last name" name="Last_Name" value={form.Last_Name} onChange={set} error={errors.Last_Name} required autoComplete="family-name" />
    <Text label="Email address" name="Email" type="email" value={form.Email} onChange={set} error={errors.Email} required placeholder="you@example.com" autoComplete="email" inputMode="email" hint="We'll send your quote confirmation here." />
    <Text label="Phone number" name="Telephone" type="tel" value={form.Telephone} onChange={set} error={errors.Telephone} placeholder="e.g. 07123 456789" autoComplete="tel" inputMode="tel" hint="Optional, but helps the surveyor reach you." />
  </div>
);

/* ---------------- Outcome views ---------------- */
const SuccessView = ({ done }) => (
  <div className="mt-8 mp-card p-8 sm:p-10 text-center" data-testid="sq-success">
    <div className="mx-auto w-14 h-14 rounded-full bg-[#10B981]/10 grid place-items-center">
      <CheckCircle2 className="w-7 h-7 text-[#10B981]" />
    </div>
    <h2 className="font-sora text-3xl font-bold text-[#0B1220] mt-4 tracking-tight">
      {done.duplicate ? "We already have your request" : "Your survey quote request has been sent"}
    </h2>
    <p className="mt-3 text-[#475569] leading-relaxed max-w-xl mx-auto">{done.message}</p>

    <div className="mt-6 inline-flex flex-col items-center gap-1 rounded-xl bg-[#F7FAF9] border border-[#DCE7E3] px-6 py-4">
      <span className="text-xs uppercase tracking-widest text-[#0F766E]">Your MortgagePath reference</span>
      <span className="font-sora text-lg font-semibold text-[#0B1220]" data-testid="sq-reference">{done.reference}</span>
      <span className="text-xs text-[#475569]">Keep this for your records.</span>
    </div>
    {done.quote && (
      <p className="mt-4 text-sm text-[#0B1220]">Indicative quote: <strong data-testid="sq-quote">{done.quote}</strong></p>
    )}

    <div className="mt-8 max-w-md mx-auto text-left rounded-2xl border border-[#DCE7E3] bg-white p-6" data-testid="sq-next-steps">
      <div className="text-xs uppercase tracking-widest text-[#0F766E]">What happens next</div>
      <ul className="mt-3 space-y-2.5">
        {NEXT_STEPS.map((s) => (
          <li key={s.slice(0, 20)} className="flex gap-3 text-sm text-[#475569] leading-relaxed">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#10B981] shrink-0" aria-hidden="true" />
            <span>{s}</span>
          </li>
        ))}
      </ul>
    </div>

    <div className="mt-6 max-w-md mx-auto flex items-start gap-3 rounded-xl bg-[#0F766E]/5 border border-[#0F766E]/15 px-5 py-4 text-left">
      <Gift className="w-5 h-5 text-[#0F766E] shrink-0 mt-0.5" aria-hidden="true" />
      <p className="text-sm text-[#475569] leading-relaxed">
        Go ahead and complete your survey through our provider and we'll pay you <strong className="text-[#0B1220]">£50 cashback</strong>.{" "}
        <Link to="/survey-reward-terms" className="text-[#0F766E] underline">Terms apply</Link>.
      </p>
    </div>

    <p className="mt-8 text-sm text-[#475569]">While you wait, keep exploring:</p>
    <div className="mt-3 flex flex-wrap gap-3 justify-center">
      <Link to="/first-time-buyer-guide" className="mp-btn-secondary text-sm" data-testid="sq-guide">Read the buying guide</Link>
      <Link to="/calculator" className="mp-btn-secondary text-sm" data-testid="sq-calc">Try the calculator</Link>
      <Link to="/" className="mp-btn-primary text-sm" data-testid="sq-home">Back to home <ArrowRight className="w-4 h-4" /></Link>
    </div>

    <div className="mt-8 max-w-md mx-auto">
      <NextStepPromptCard
        icon={Scale}
        eyebrow="You might also need"
        title="A conveyancing quote"
        body="If you're buying, you'll also need a conveyancer for the legal side."
        to="/conveyancing-quote?source=post_survey_next_step"
        ctaEvent="conveyancing_cta_clicked"
        ctaLocation="post_survey_next_step"
      />
    </div>
  </div>
);

const FailedView = ({ onRetry }) => (
  <div className="mt-8 mp-card p-8 sm:p-10 text-center" data-testid="sq-failed">
    <div className="mx-auto w-14 h-14 rounded-full bg-[#B45309]/10 grid place-items-center">
      <AlertTriangle className="w-7 h-7 text-[#B45309]" />
    </div>
    <h2 className="font-sora text-2xl sm:text-3xl font-bold text-[#0B1220] mt-4 tracking-tight">We couldn't confirm your request</h2>
    <p className="mt-3 text-[#475569] leading-relaxed max-w-xl mx-auto">
      Something went wrong sending your request to our survey partner, so we can't confirm it was received. Please try
      again in a few minutes. If it keeps happening, email us at{" "}
      <a href="mailto:hello@mortgagepath.uk" className="text-[#0F766E] underline">hello@mortgagepath.uk</a> and we'll help.
    </p>
    <div className="mt-6 flex flex-wrap gap-3 justify-center">
      <button type="button" onClick={onRetry} className="mp-btn-primary" data-testid="sq-retry">Try again <ArrowRight className="w-4 h-4" /></button>
      <Link to="/" className="mp-btn-secondary" data-testid="sq-failed-home">Back to home</Link>
    </div>
  </div>
);

/* ===================================================================== */
const SurveyQuote = () => {
  const [params] = useSearchParams();
  const source = params.get("source") || "direct";
  const [opts, setOpts] = useState(FALLBACK);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [failed, setFailed] = useState(false);
  const startedRef = useRef(false);
  const formRef = useRef(null);

  useEffect(() => {
    track("survey_quote_view", { source });
    api.get("/survey/config")
      .then(({ data }) => setOpts((prev) => ({ ...prev, ...data })))
      .catch(() => {});
  }, [source]);

  const markStarted = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      track("survey_quote_start", { source });
    }
  };

  const set = (name, value) => {
    markStarted();
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => (e[name] ? { ...e, [name]: undefined } : e));
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onHeroStart = () => {
    markStarted();
    scrollToForm();
    setTimeout(() => {
      document.querySelector("#sq-step-region input, #sq-step-region select")?.focus();
    }, 420);
  };

  const validateStep = (s) => {
    const errs = {};
    if (s === 1) {
      if (!form.Job_Address_Line_1.trim()) errs.Job_Address_Line_1 = "Please enter address line 1.";
      if (!form.Job_Address_Town_City.trim()) errs.Job_Address_Town_City = "Please enter town or city.";
      if (!form.Job_Address_Postcode.trim()) errs.Job_Address_Postcode = "Please enter a postcode.";
      if (form.Property_Value && !/^\d[\d,]*$/.test(form.Property_Value.trim())) errs.Property_Value = "Please enter a number.";
      if (form.Property_Bedrooms && !/^\d+$/.test(form.Property_Bedrooms.trim())) errs.Property_Bedrooms = "Please enter a number.";
      if (form.Property_Link && !/^https?:\/\/\S+$/i.test(form.Property_Link.trim())) errs.Property_Link = "Please enter a valid URL.";
    } else if (s === 2) {
      if (!form.Preferred_Service) errs.Preferred_Service = "Please choose a survey type.";
    } else if (s === 3) {
      if (!form.First_Name.trim()) errs.First_Name = "Please enter your first name.";
      if (!form.Last_Name.trim()) errs.Last_Name = "Please enter your last name.";
      if (!form.Email.trim()) errs.Email = "Please enter your email address.";
      else if (!EMAIL_RE.test(form.Email)) errs.Email = "Please enter a valid email address.";
    }
    return errs;
  };

  const goNext = () => {
    const errs = validateStep(step);
    if (Object.keys(errs).length) {
      setErrors((e) => ({ ...e, ...errs }));
      toast.error("Please complete the required fields.");
      return;
    }
    track(`survey_quote_step_${step}_complete`, { source });
    setStep((s) => s + 1);
    scrollToForm();
  };

  const goBack = () => {
    setStep((s) => Math.max(1, s - 1));
    scrollToForm();
  };

  const onRequestQuote = (e) => {
    e.preventDefault();
    const errs = validateStep(3);
    if (Object.keys(errs).length) {
      setErrors((err) => ({ ...err, ...errs }));
      toast.error("Please complete the required fields.");
      return;
    }
    setConfirmOpen(true);
  };

  /* Only here do we transmit to the e.surv referral integration. The payload
     is identical to the original single-page form: { ...form, consent, source }. */
  const onConfirmedSubmit = async () => {
    setSubmitting(true);
    track("survey_quote_submit", { source, service: form.Preferred_Service });
    try {
      const { data } = await api.post("/survey/lead", { ...form, consent: true, source });
      const genuinelyDelivered = data.delivered === true;
      const priorSuccessDuplicate = data.duplicate === true && data.status !== "post_failed";

      if (genuinelyDelivered || priorSuccessDuplicate) {
        // survey_quote_success fires ONLY for a genuine, newly delivered lead —
        // never for duplicates or soft/failed submissions (avoids inflating leads).
        if (genuinelyDelivered && !data.duplicate) {
          track("survey_quote_success", {
            source,
            lead_reference: data.reference,
            service: form.Preferred_Service,
          });
          // Meta standard Lead — fired once per confirmed e.surv delivery only.
          fireMetaLeadOnce(data.reference);
        }
        setDone(data);
        setConfirmOpen(false);
        toast.success(data.duplicate ? "We already have your request." : "Your survey quote request has been sent.");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        // delivered:false (or a duplicate of a previously failed lead) — delivery
        // to e.surv is NOT confirmed, so we must NOT show a success state.
        setConfirmOpen(false);
        setFailed(true);
        track("survey_quote_error", { source, status: "post_not_confirmed" });
        toast.error("We couldn't confirm your request reached the survey partner.");
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 422) {
        const errs = err?.response?.data?.detail?.errors || {};
        setErrors(errs);
        setConfirmOpen(false);
        // Jump to the earliest step that has an error so the user can fix it.
        if (errs.Job_Address_Line_1 || errs.Job_Address_Town_City || errs.Job_Address_Postcode || errs.Property_Value || errs.Property_Bedrooms || errs.Property_Link) setStep(1);
        else if (errs.Preferred_Service) setStep(2);
        else setStep(3);
        toast.error("Please fix the highlighted fields.");
      } else {
        const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
        toast.error(typeof msg === "string" ? msg : "Please try again.");
      }
      track("survey_quote_error", { source, status: status || 0 });
    } finally {
      setSubmitting(false);
    }
  };

  const onRetry = () => {
    setFailed(false);
    setStep(3);
    setTimeout(scrollToForm, 50);
  };

  const showForm = !done && !failed;

  return (
    <div className="mp-container py-10 sm:py-14 max-w-4xl">
      <Seo
        title={PAGES["/survey-quote"].title}
        description={PAGES["/survey-quote"].description}
        path="/survey-quote"
        noindex
      />

      {done && <SuccessView done={done} />}
      {failed && <FailedView onRetry={onRetry} />}

      {showForm && (
        <>
          <Hero onStart={onHeroStart} />

          <div ref={formRef} className="mt-8 scroll-mt-20" id="survey-form">
            <div className="mp-card p-6 sm:p-7">
              <ProgressBar step={step} />

              <h2 className="font-sora text-lg font-semibold text-[#0B1220] mt-6">{STEP_TITLES[step - 1]}</h2>

              <form
                className="mt-5"
                id="sq-step-region"
                onSubmit={step === 3 ? onRequestQuote : (e) => { e.preventDefault(); goNext(); }}
                noValidate
                data-testid="sq-form"
              >
                {step === 1 && <StepProperty form={form} set={set} errors={errors} opts={opts} />}
                {step === 2 && <StepSurvey form={form} set={set} errors={errors} opts={opts} />}
                {step === 3 && <StepContact form={form} set={set} errors={errors} opts={opts} />}

                {step === 3 && (
                  <p className="mt-5 text-xs text-[#475569] leading-relaxed">
                    Nothing is shared with a third party at this stage. You'll be asked to confirm consent on the next
                    screen before we transmit your details. MortgagePath is not a surveyor and may receive a referral fee.
                  </p>
                )}

                <div className="mt-6 flex items-center justify-between gap-3">
                  {step > 1 ? (
                    <button type="button" onClick={goBack} className="mp-btn-secondary" data-testid="sq-back">
                      <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                  ) : <span />}

                  {step < 3 ? (
                    <button type="submit" className="mp-btn-primary" data-testid="sq-next">
                      Continue <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button type="submit" className="mp-btn-primary" data-testid="sq-submit">
                      Request my survey quote <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* Trust / reassurance beside the form */}
            <div className="mt-4 flex items-start gap-3 rounded-xl border border-[#DCE7E3] bg-[#F7FAF9] px-5 py-4" data-testid="sq-trust">
              <ShieldCheck className="w-5 h-5 text-[#0F766E] shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-[#475569] leading-relaxed">
                MortgagePath helps homebuyers understand their options and access trusted UK property services. Your
                survey will be provided by our specialist survey partner.
              </p>
            </div>
          </div>
        </>
      )}

      <PartnerConsentDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        providerName="e.surv Chartered Surveyors"
        headline="Confirm your survey quote request"
        summary="our selected survey provider, so they can provide your requested quote and contact you"
        checkboxLabel={
          <>
            I consent to MortgagePath sharing the details I have provided with{" "}
            <span className="font-semibold">e.surv Chartered Surveyors</span> for the purpose of providing my
            survey quote and contacting me about it. See our{" "}
            <Link to="/privacy" className="text-[#0F766E] underline">Privacy Policy</Link>.
          </>
        }
        submitting={submitting}
        onConfirm={onConfirmedSubmit}
        testidPrefix="sq-consent"
      />

      <SeoProse sections={PAGES["/survey-quote"].sections} className="mt-14" />
      <div className="mt-8"><ComplianceStrip /></div>
    </div>
  );
};

export default SurveyQuote;
